import { isStoredAuthenticated } from '@api/api-client';
import type { UserResponse } from '@api/users-api';
import { getUsers } from '@api/users-api';
import { AlertTriangle, Award, ChevronRight, Loader2, LogIn, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const AVATAR_COLORS = ['#007c68', '#2563eb', '#7c3aed', '#dc2626', '#ea580c'] as const;
const RANDOM_USER_COUNT = 5;

function getEmailName(email: string): string {
  const local = email.split('@')[0] ?? '';
  return local
    .split('.')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getInitials(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split('.');
  const first = parts[0] ?? '';
  const second = parts[1];
  if (second) {
    return `${first.charAt(0)}${second.charAt(0)}`.toUpperCase();
  }
  return first.slice(0, 2).toUpperCase();
}

function pickRandom<T>(items: T[], count: number): T[] {
  const shuffled = [...items].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function UserCard({
  user,
  color,
  index,
  onSelect,
}: {
  readonly user: UserResponse;
  readonly color: string;
  readonly index: number;
  readonly onSelect: (user: UserResponse) => void;
}): React.JSX.Element {
  const displayName =
    user.name && user.surname ? `${user.name} ${user.surname}` : getEmailName(user.email);

  const handleClick = (): void => {
    onSelect(user);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(user);
    }
  };

  const professionalType = user.docplannerClinicId ? 'Clínica' : 'Doctor';

  return (
    <div
      className={`vs-user-card vs-animate vs-animate--delay-${index + 1}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Acceder como ${displayName}`}
    >
      <div className="vs-user-card__avatar" style={{ background: color }}>
        {getInitials(user.email)}
      </div>

      <div className="vs-user-card__name">{displayName}</div>
      <div className="vs-user-card__email">{user.email}</div>

      <div className="vs-user-card__tags">
        <span className="vs-user-card__tag vs-user-card__tag--can-vote">
          <ShieldCheck size={12} /> Puede votar
        </span>
        <span className="vs-user-card__tag">{professionalType}</span>
      </div>

      <span className="vs-user-card__cta">
        Acceder
        <ChevronRight size={16} />
      </span>
    </div>
  );
}

export default function VotingLogin(): React.JSX.Element {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);

  useEffect(() => {
    if (!isStoredAuthenticated()) {
      setNeedsAuth(true);
      setLoading(false);
      return;
    }

    async function fetchUsers(): Promise<void> {
      try {
        const usersResponse = await getUsers();
        setUsers(pickRandom(usersResponse.data, RANDOM_USER_COUNT));
      } catch (err) {
        console.error('Failed to load users:', err);
        setError('Error al cargar los usuarios.');
      } finally {
        setLoading(false);
      }
    }

    fetchUsers();
  }, []);

  const handleSelectUser = (user: UserResponse): void => {
    navigate('/voting/vote', { state: { user } });
  };

  return (
    <div className="vs-page">
      <nav className="vs-nav">
        <span className="vs-nav__brand">
          <span className="vs-nav__logo">
            <Award size={20} />
          </span>
          DP Awards
        </span>
      </nav>

      <div className="vs-login">
        <div className="vs-login__hero vs-animate">
          <span className="vs-login__badge">
            <Award size={14} />
            Simulación de votación
          </span>
          <h1 className="vs-login__title">Elige tu perfil para votar</h1>
          <p className="vs-login__subtitle">
            Selecciona uno de los usuarios disponibles para simular el proceso de votación en la
            edición activa de DP Awards.
          </p>
        </div>

        {needsAuth && (
          <div className="vs-auth-required vs-animate">
            <LogIn size={40} />
            <p>Inicia sesión en el panel de administración para acceder a la simulación.</p>
            <Link to="/" className="vs-auth-required__link">
              Ir al login
            </Link>
          </div>
        )}

        {loading && !needsAuth && (
          <div className="vs-loading">
            <Loader2 size={32} className="vs-vote-btn__spinner" />
            <p>Cargando usuarios...</p>
          </div>
        )}

        {error && (
          <div className="vs-loading">
            <AlertTriangle size={32} />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && !needsAuth && (
          <div className="vs-login__grid">
            {users.map((user, index) => (
              <UserCard
                key={user.id}
                user={user}
                color={AVATAR_COLORS[index % AVATAR_COLORS.length] ?? '#007c68'}
                index={index}
                onSelect={handleSelectUser}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
